import { useEffect, useState } from 'react'

// Settings for marketing automation.
//
// One settings row per restaurant — the upsert in saveSettings
// creates it on first save, updates it after that. Customer can
// toggle the whole feature off without losing their preferences.
//
// Mirrors PromotionEditModal: bottom-sheet on mobile, reset on
// open via useEffect, setSubmitting in finally to avoid the
// modal-stays-mounted lock-up bug.

const PLATFORM_OPTIONS = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok',    label: 'TikTok' },
  { id: 'facebook',  label: 'Facebook' },
]

const TONE_OPTIONS = [
  { id: 'operator',     label: 'Operator',     hint: 'Direct, kitchen-floor energy.' },
  { id: 'professional', label: 'Professional', hint: 'Polished, brand-forward.' },
  { id: 'playful',      label: 'Playful',      hint: 'Loose, jokes welcome.' },
]

const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']

export default function MarketingSettingsModal({
  open,
  onClose,
  onSave,        // (patch) => Promise<void>  — wraps useMarketingDrafts.saveSettings
  settings,      // existing settings row, or null when first-time
  restaurantId,  // required for upsert when settings is null
}) {
  const [form, setForm]             = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState(null)

  useEffect(() => {
    if (!open) return
    // Default values match the migration's column defaults.
    setForm({
      enabled:          settings?.enabled         ?? true,
      cadence:          settings?.cadence         ?? 'weekly',
      generation_day:   settings?.generation_day  ?? 1,
      generation_hour:  settings?.generation_hour ?? 8,
      tone_preference:  settings?.tone_preference ?? 'operator',
      platforms:        Array.isArray(settings?.platforms) && settings.platforms.length
                          ? settings.platforms
                          : ['instagram','tiktok'],
      voice_notes:      settings?.voice_notes ?? '',
      drafts_per_cycle: settings?.drafts_per_cycle ?? 5,
    })
    setError(null)
    setSubmitting(false)
  }, [open, settings])

  if (!open || !form) return null

  function set(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function togglePlatform(p) {
    setForm((prev) => {
      const has = prev.platforms.includes(p)
      return {
        ...prev,
        platforms: has ? prev.platforms.filter((x) => x !== p) : [...prev.platforms, p],
      }
    })
  }

  async function handleSubmit(e) {
    e?.preventDefault?.()
    if (submitting) return
    if (form.enabled && form.platforms.length === 0) {
      setError('Pick at least one platform when automation is on.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const payload = {
        // restaurant_id is filled by the trigger if we omit it,
        // but we pass it explicitly so the upsert finds the row.
        ...(restaurantId ? { restaurant_id: restaurantId } : {}),
        enabled:          !!form.enabled,
        cadence:          form.cadence,
        generation_day:   Number(form.generation_day),
        generation_hour:  Number(form.generation_hour),
        tone_preference:  form.tone_preference,
        platforms:        form.platforms,
        voice_notes:      form.voice_notes.trim() || null,
        drafts_per_cycle: Number(form.drafts_per_cycle),
      }
      await onSave(payload)
      onClose?.()
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[MarketingSettingsModal] save failed', e2)
      setError(`Could not save: ${e2?.message || 'try again.'}`)
    } finally {
      setSubmitting(false)
    }
  }

  const isFirstTime = !settings

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <form
        onSubmit={handleSubmit}
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Marketing automation settings"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium truncate">
              {isFirstTime ? 'Enable marketing automation' : 'Marketing automation settings'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-5 px-4 pb-6">
          {isFirstTime && (
            <p className="rounded-lg bg-blue-50 px-3 py-3 text-sm text-blue-900">
              We'll generate ready-to-publish social media drafts for you on the schedule
              you pick below — using your real menu items and current promotions. Drafts
              land in your Marketing → Drafts inbox. You approve, edit, or reject. Posting
              is still up to you (paste into Meta Business Suite or TikTok).
            </p>
          )}

          {/* On/Off */}
          <label className="flex items-center justify-between rounded-lg border border-black/10 px-3 py-3">
            <div>
              <div className="font-medium">Generate drafts automatically</div>
              <div className="text-xs text-black/55">Turn off any time. Existing drafts stay.</div>
            </div>
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => set('enabled', e.target.checked)}
              className="h-6 w-6 accent-blue-600"
              aria-label="Enable marketing automation"
            />
          </label>

          {/* Cadence */}
          <div>
            <div className="mb-1 text-sm font-medium">Cadence</div>
            <div className="grid grid-cols-2 gap-2">
              {['weekly','daily'].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set('cadence', c)}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium capitalize ${
                    form.cadence === c
                      ? 'border-blue-600 bg-blue-50 text-blue-900'
                      : 'border-black/15 bg-white text-black/70 hover:bg-black/5'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Day (weekly only) */}
          {form.cadence === 'weekly' && (
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="ms-day">
                Generation day
              </label>
              <select
                id="ms-day"
                value={form.generation_day}
                onChange={(e) => set('generation_day', e.target.value)}
                className="w-full rounded-lg border border-black/15 px-3 py-2 text-base"
              >
                {DAY_NAMES.map((name, i) => (
                  <option key={i} value={i}>{name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Hour */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="ms-hour">
              Generation time (your local hour)
            </label>
            <select
              id="ms-hour"
              value={form.generation_hour}
              onChange={(e) => set('generation_hour', e.target.value)}
              className="w-full rounded-lg border border-black/15 px-3 py-2 text-base"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {formatHour(h)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-black/55">
              Drafts arrive in your inbox by this time. Pick something quiet — early morning is ideal.
            </p>
          </div>

          {/* Platforms */}
          <div>
            <div className="mb-1 text-sm font-medium">Platforms to draft for</div>
            <div className="flex flex-wrap gap-2">
              {PLATFORM_OPTIONS.map((p) => {
                const active = form.platforms.includes(p.id)
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePlatform(p.id)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium ${
                      active
                        ? 'border-blue-600 bg-blue-50 text-blue-900'
                        : 'border-black/15 bg-white text-black/70 hover:bg-black/5'
                    }`}
                  >
                    {p.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Tone */}
          <div>
            <div className="mb-1 text-sm font-medium">Tone preference</div>
            <div className="space-y-2">
              {TONE_OPTIONS.map((t) => {
                const active = form.tone_preference === t.id
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => set('tone_preference', t.id)}
                    className={`block w-full rounded-lg border px-3 py-2 text-left ${
                      active
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-black/15 bg-white hover:bg-black/5'
                    }`}
                  >
                    <div className="font-medium">{t.label}</div>
                    <div className="text-xs text-black/55">{t.hint}</div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Voice notes — capped at 500 chars so it stays bounded
              in the system prompt and can't be used to inject huge
              walls of context. Counter shows remaining. */}
          <div>
            <label className="mb-1 flex items-baseline justify-between text-sm font-medium" htmlFor="ms-voice">
              <span>Voice notes <span className="text-black/40 font-normal">(optional)</span></span>
              <span className={`text-xs font-normal ${form.voice_notes.length > 480 ? 'text-amber-700' : 'text-black/40'}`}>
                {form.voice_notes.length}/500
              </span>
            </label>
            <textarea
              id="ms-voice"
              value={form.voice_notes}
              onChange={(e) => set('voice_notes', e.target.value.slice(0, 500))}
              maxLength={500}
              placeholder="Anything specific the AI should know — house style, things to avoid, signature dishes to lean on…"
              rows={3}
              className="w-full rounded-lg border border-black/15 px-3 py-2 text-base"
            />
          </div>

          {/* Drafts per cycle */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="ms-count">
              Drafts per cycle
            </label>
            <input
              id="ms-count"
              type="number"
              min={1}
              max={25}
              value={form.drafts_per_cycle}
              onChange={(e) => set('drafts_per_cycle', e.target.value)}
              className="w-full rounded-lg border border-black/15 px-3 py-2 text-base"
            />
            <p className="mt-1 text-xs text-black/55">
              5 is plenty for one location. Bump higher if you post daily.
            </p>
          </div>

          {/* Schedule preview — surfaces what's about to be saved so the
              manager isn't left guessing whether the cron is wired up. */}
          {form.enabled && form.platforms.length > 0 && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
              <div className="text-[10px] font-medium text-emerald-800">
                Scheduled
              </div>
              <div className="mt-1 text-sm text-emerald-900">
                {form.cadence === 'daily'
                  ? <>Drafts will be generated <strong>every day at {formatHour(Number(form.generation_hour))}</strong> in your timezone.</>
                  : <>Drafts will be generated <strong>every {DAY_NAMES[Number(form.generation_day)]} at {formatHour(Number(form.generation_hour))}</strong> in your timezone.</>
                }
                {' '}
                {Number(form.drafts_per_cycle)} draft{Number(form.drafts_per_cycle) === 1 ? '' : 's'} per cycle
                {' '}for{' '}
                {form.platforms.join(', ')}.
              </div>
              <div className="mt-1 text-[11px] text-emerald-800/80">
                You can also tap "Generate now" anytime to create drafts on demand.
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Saving…' : (isFirstTime ? 'Enable automation' : 'Save settings')}
          </button>
        </div>
      </form>
    </div>
  )
}

function formatHour(h) {
  if (h === 0)  return '12:00 AM'
  if (h === 12) return '12:00 PM'
  if (h < 12)   return `${h}:00 AM`
  return `${h - 12}:00 PM`
}
